import { HttpClient, HttpResponse } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import {
	ContactAddress,
	ContactDetails,
	ContactsResponseMapScope,
	CountriesResponseMap,
	Country,
	CreateContact,
	FilterContact,
	LastContactModificationsResponse,
	LinkContactsToContacts,
	LinkEnterprisesToContacts,
	TaxValidator,
	TaxValidatorResponse,
	UpdateAddressTagsAssigned,
	UpdateTagsAssigned
} from '@interfaces'
import { BehaviorSubject, Observable, map } from 'rxjs'

@Injectable({
	providedIn: 'root'
})
export class ContactService {
	private http = inject(HttpClient)
	url = '/customers/contacts'

	// Observable para la gestión de etiquetas
	contactDetailsSubject = new BehaviorSubject<ContactDetails | undefined>(undefined)
	$contactDetails = this.contactDetailsSubject.asObservable()

	// Observable para la edición de contacto en detalles
	isEditingContactSubject = new BehaviorSubject<boolean>(false)
	$isEditingContact = this.isEditingContactSubject.asObservable()

	// Observable para saber si el formulario de edición está 'sucio'
	isFormDirtySubject = new BehaviorSubject<boolean>(false)
	$isFormDirty = this.isFormDirtySubject.asObservable()

	// Observable para saber si hay que actualizar el detalle de contacto
	updateContactDetailsSubject = new BehaviorSubject<boolean>(false)
	$updateContactDetails = this.updateContactDetailsSubject.asObservable()

	//Observable para saber si hay que actualizar la lista de contactos
	updateContactsListSubject = new BehaviorSubject<boolean>(false)
	$updateContactsList = this.updateContactsListSubject.asObservable()

	// Llamada a la API para añadir una nueva dirección a una empresa
	addNewAddressToContact(contactId: number, address: ContactAddress): Observable<ContactDetails> {
		return this.http.put<ContactDetails>(`${this.url}/${contactId}/address`, address)
	}

	// Reasigna el usuario a los contactos seleccionados
	changeAssignedUser(userId: number, contacts: { id: number }[]) {
		return this.http.post<ContactsResponseMapScope>(`${this.url}/user?idUser=${userId}`, contacts)
	}

	// Crea un contacto
	createOrUpdateContact(data: CreateContact): Observable<ContactDetails | ContactDetails> {
		return this.http.post<ContactDetails | ContactDetails>(`${this.url}`, data)
	}

	// Elimina un contacto
	deleteContact(id: number) {
		return this.http.delete<void>(`${this.url}/${id}`)
	}

	// Llamada al endpoint para eliminar una dirección de una empresa
	deleteAddressToContact(contactId: number, addressId: number) {
		return this.http.delete<void>(`${this.url}/${contactId}/address/${addressId}`)
	}

	// Elimina una etiqueta de un contacto
	deleteAssignedTags(contactId: number, tagId: number): Observable<void> {
		return this.http.delete<void>(`${this.url}/${contactId}/tag/${tagId}`)
	}

	// Elimina una etiqueta de una dirección en conctreto de un contacto
	deleteAddressAssignedTags(contactId: number, addressId: number, tagId: number): Observable<void> {
		return this.http.delete<void>(`${this.url}/${contactId}/address/${addressId}/tag/${tagId}`)
	}

	// Elimina un contacto vinculado con un contacto en concreto
	deleteLinkedContact(contactId: number, associatedId: number): Observable<void> {
		return this.http.delete<void>(`${this.url}/${contactId}/associatedContact/${associatedId}`)
	}

	// Elimina una empresa vinculada con un contacto en concreto
	deleteLinkedEnterprise(contactId: number, associatedId: number): Observable<void> {
		return this.http.delete<void>(`${this.url}/${contactId}/associatedEnterprise/${associatedId}`)
	}

	// Devuelve un blob para descargar el excel con los contactos seleccionados
	exportContacts(ids: Set<number>): Observable<HttpResponse<Blob>> {
		const selectedContacts = ids.size ? { ids: Array.from(ids) } : {}
		return this.http.post<Blob>(`${this.url}/export`, selectedContacts, {
			observe: 'response',
			responseType: 'blob' as 'json'
		})
	}

	// Devuelve los contactos paginados
	getContacts(page = 0, size = 20, genericFilter = ''): Observable<ContactsResponseMapScope> {
		return this.http.get<ContactsResponseMapScope>(`${this.url}`, { params: { page, size, genericFilter } })
	}

	// Devuelve todos los contactos
	getAllContacts(): Observable<ContactsResponseMapScope> {
		return this.http.get<ContactsResponseMapScope>(`${this.url}`)
	}

	// Recoge la lista de contactos filtrados
	getFilteredContacts(data: FilterContact): Observable<ContactsResponseMapScope> {
		return this.http.post<ContactsResponseMapScope>(`${this.url}/search`, data)
	}

	// Devuelve el detalle del contacto
	getContactDetails(contactId: number): Observable<ContactDetails> {
		return this.http.get<ContactDetails>(`${this.url}/${contactId}`)
	}

	// Llamada al endpoint para obtener el listado de todos los países
	getCountries(): Observable<Country[]> {
		return this.http
			.get<CountriesResponseMap>(`/customers/countries`)
			.pipe(
				map(countries =>
					countries.content.sort(
						(a, b) => b.priorityOrder - a.priorityOrder || a.description.localeCompare(b.description)
					)
				)
			)
	}

	// Llamada al endpoint para obtener todo el listado paginado de las últimas modificaciones de la empresa
	getLastModifications(contactId: number, page = 0, size = 5): Observable<LastContactModificationsResponse> {
		return this.http.get<LastContactModificationsResponse>(
			`${this.url}/${contactId}/lastModifications?page=${page}&size=${size}`
		)
	}

	// Desactiva el estado de edición
	resetIsEditingContact() {
		this.isEditingContactSubject.next(false)
	}

	// Setea la edición del contacto
	setIsEditingContact(editing: boolean = true) {
		this.isEditingContactSubject.next(editing)
	}

	// Setea el formDirty
	setIsFormDirty(isDirty: boolean = true) {
		this.isFormDirtySubject.next(isDirty)
	}

	// Setea el listado del contacto
	setUpdateContactsList() {
		this.updateContactsListSubject.next(true)
	}

	// Llamada al endpoint para validar un código fiscal
	taxValidator(fiscalCode: TaxValidator): Observable<TaxValidatorResponse> {
		return this.http.post<TaxValidatorResponse>(`/tax-validator/validate`, fiscalCode)
	}

	// Actualiza las etiquetas de un contacto
	updateAssignedTags(data: UpdateTagsAssigned): Observable<void> {
		return this.http.post<void>(`${this.url}/tag`, data)
	}

	// Actualiza las etiquetas de una dirección del contacto
	updateAddressAssignedTags(data: UpdateAddressTagsAssigned, contactId: number, addressId: number): Observable<void> {
		return this.http.post<void>(`${this.url}/${contactId}/address/${addressId}/tag`, data)
	}

	// Llamada al endpoint para actualizar los contactos asociados a una empresa
	updateLinkedContacts(data: LinkContactsToContacts, id: number): Observable<void> {
		return this.http.post<void>(`${this.url}/${id}/associatedContact`, data)
	}

	// Llamada al endpoint para actualizar las empresas asociadas a una empresa
	updateLinkedEnterprises(data: LinkEnterprisesToContacts, id: number): Observable<void> {
		return this.http.post<void>(`${this.url}/${id}/associatedEnterprise`, data)
	}
}
